import { useEffect, useState } from "react";
import dayjs from "dayjs";

import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Tooltip,
  Space,
  Checkbox,
  Col,
  Row,
  InputNumber,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { BOM } from "../../../../../services/definedMethod";

// Prodcut Type not available

export default function ProductInformationContent({
  form,
  onNext,
  interbankTemplate,
  onCurrencyChange,
  productCategoryOptions,
}) {
  const [allowCollatoral, setAllowCollatoral] = useState(false);

  const handleInputChange = (field, value) => {
    form.setFieldValue(field, value);
  };
  const selectedCurrency = Form.useWatch("currencyCode", form);

  useEffect(() => {
    if (onCurrencyChange && selectedCurrency) {
      onCurrencyChange(selectedCurrency);
    }
  }, [selectedCurrency]);

  const handleNext = async () => {
    try {
      // Validate only product information fields
      await form.validateFields([
        "productCode",
        "productName",
        "productCategory",
        "currencyCode",
        "applicableEntities",
        "custodian",
        "placementType",
        "startDate",
        "endDate",
      ]);

      if (allowCollatoral) {
        await form.validateFields([
          "collateralTypeId",
          "collateralVolume",
          "collateralNumber",
          "collateralValue",
          "collateralMaturityDate",
        ]);
      }
      onNext(); // Navigate to next tab
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Card
      style={{
        borderRadius: "12px",
        boxShadow:
          "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "32px",
        }}
      >
        <h1
          style={{
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "32px",
            color: "#101828",
            marginBottom: "0px",
          }}
        >
          Product Information
        </h1>
        <p
          style={{
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#344054",
            marginTop: "0px",
          }}
        >
          The descriptive label for the interbank placement product, e.g.,
          “Interbank Fixed Deposit – 30 Days” or “Call Placement”.
        </p>
      </div>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: "100%" }}
        requiredMark={false}
      >
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Product Code
                  <Tooltip title="Product code information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="productCode"
              rules={[
                {
                  required: true,
                  message: "Product code cannot be empty",
                },
                {
                  pattern: /^[A-Z0-9]+$/,
                  message:
                    "Product code must contain only uppercase letters and numbers",
                },
                {
                  max: 5,
                  message: "Product code cannot be more than 5 characters",
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input
                placeholder="Enter product code"
                style={{ height: "44px" }}
                onChange={(e) =>
                  handleInputChange("productCode", e.target.value)
                }
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Product Name
                  <Tooltip title="A unique identifier assigned to the interbank placement product for internal tracking, processing, and reporting within the banking system.">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="productName"
              rules={[
                {
                  required: true,
                  message: "Please enter product name",
                },
                {
                  min: 3,
                  message: "Product Name must be at least 3 characters",
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input
                placeholder="Enter product name"
                style={{ height: "44px" }}
                onChange={(e) =>
                  handleInputChange("productName", e.target.value)
                }
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Product Category
                  <Tooltip title="A classification that defines the type of investment - i.e. short term fixed income or long term fixed income">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="productCategory"
              rules={[
                { required: true, message: "Please select product category" },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Select
                placeholder="Select product category"
                style={{ height: "44px" }}
                onChange={(value) =>
                  handleInputChange("productCategory", value)
                }
                // options={productCategoryOptions}
                options={interbankTemplate?.productCategories.map((option) => ({
                  value: option.value,
                  label: option.text,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Currency
                  <Tooltip title="The legal tender in which the placement is made, e.g. Naira">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="currencyCode"
              // rules={[{ required: true, message: 'Please select currency' }]}
              // validateTrigger={['onChange', 'onBlur']}
            >
              <Select
                placeholder="Select currency"
                style={{ height: "44px" }}
                onChange={(value) => handleInputChange("currencyCode", value)}
                options={interbankTemplate?.currencies?.map((item) => ({
                  value: item?.code,
                  label: item?.displayLabel,
                  symbol: item?.displaySymbol,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Applicable Entities
                  <Tooltip title="Applicable entities information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="applicableEntities"
              rules={[
                {
                  required: true,
                  message: "Please select applicable entities",
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Select
                placeholder="Select applicable entities"
                style={{ width: "100%" }}
                mode="multiple"
                onChange={(value) =>
                  handleInputChange("applicableEntities", value)
                }
                options={interbankTemplate?.applicableEntityOptions?.map(
                  (item) => ({
                    value: item?.id,
                    label: item?.name,
                  })
                )}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Accounting Classification
                  <Tooltip title="Select accounting Classification">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="accountingClassification"
              rules={[
                {
                  required: true,
                  message: "Please select accounting classification",
                },
              ]}
            >
              <Select
                options={interbankTemplate?.accountingClassifications.map(
                  (option) => ({
                    value: option.id,
                    label: option.name,
                  })
                )}
                placeholder={"Select accounting classification"}
                style={{ height: 44 }}
                showSearch
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Custodian
                  <Tooltip title="Select custodian">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="custodian"
              rules={[
                {
                  required: true,
                  message: "Please select custodian",
                },
              ]}
            >
              <Select
                options={interbankTemplate?.custodians.map((option) => ({
                  value: option.id,
                  label: option.name,
                }))}
                placeholder={"Select custodian"}
                style={{ height: 44 }}
                showSearch
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label="Start Date"
              name="startDate"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[
                { required: true, message: "Please select start date" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const endDate = getFieldValue("endDate");
                    if (!value || !endDate || !value.isSame(endDate, "day")) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Start date cannot be the same as end date")
                    );
                  },
                }),
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <DatePicker
                style={{ width: "100%", height: "44px", marginBottom: 0 }}
                placeholder="Select date"
                onChange={(date) => handleInputChange("startDate", date)}
                disabledDate={(current) => {
                  const startOfYear = dayjs().startOf("year");
                  return (
                    current && (current > dayjs() || current < startOfYear)
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label="End Date"
              name="endDate"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: "Please select end date" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("startDate");
                    // If either value or startDate is not provided, skip validation
                    if (!value || !startDate) {
                      return Promise.resolve();
                    }
                    // Check if endDate is before or the same as startDate
                    if (
                      value.isBefore(startDate, "day") ||
                      value.isSame(startDate, "day")
                    ) {
                      return Promise.reject(
                        new Error("End date must be after the start date")
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <DatePicker
                style={{ width: "100%", height: "44px", marginBottom: "0px" }}
                placeholder="Select end date"
                onChange={(date) => handleInputChange("endDate", date)}
              />
            </Form.Item>
          </Col>
        </Row>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "32px",
            gap: "12px",
          }}
        >
          <Button
            type="primary"
            style={{ backgroundColor: "#9F1AB1", height: "44px" }}
            onClick={handleNext}
          >
            Next - Terms
          </Button>
        </div>
      </Form>
    </Card>
  );
}
